import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../../Services/AuthService';
import TextSimpleEditor from '../../Components/Admin/TextSimpleEditor';
import TextSelectableEditor from '../../Components/Admin/TextSelectableEditor';
import TextSelectableArchitectEditor from '../../Components/Admin/TextSelectableArchitectEditor';
import QuestionSimpleEditor from '../../Components/Admin/QuestionSimpleEditor';
import QuestionMultipleEditor from '../../Components/Admin/QuestionMultipleEditor';
import QuestionImageEditor from '../../Components/Admin/QuestionImageEditor';
import ContentViewerReadOnly from '../../Components/Architect/ContentViewerReadOnly';
import { htmlToPreviewText } from '../../utils/textUtils';

interface ProjectDetailResponse {
    project: ProjectDetail;
    projectGroups: ProjectGroup[];
}

interface ProjectDetail {
    projectId: string;
    internalName: string;
    externalName: string;
    interventionType: 'NEW_CONSTRUCTION' | 'RENOVATION' | string;
    surface: string;
    clientEmail: string;
    clientSize: 'SINGULAR' | 'PLURAL' | string;
    clientTone: 'INFORMAL' | 'FORMAL' | string;
    ownerId: string;
    clientId: string | null;
    createdAt: string;
    updatedAt: string;
}

interface ProjectGroup {
    projectGroupId: string;
    projectId: string;
    templateGroupId: string;
    groupName: string;
    position: number;
    groupSubtitle: string | null;
    groupTimeDuration: string | null;
    projectSections: ProjectSection[];
    submitted: boolean;
}

interface ProjectSection {
    projectSectionId: string;
    projectId: string;
    projectGroupId: string;
    templateSectionId: string;
    sectionName: string;
    position: number;
    enabled: boolean;
    labels: string[];
    recommendationText: string | null;
    featured: boolean;
    sectionSummary: string | null;
    sectionPurpose: string | null;
    sectionTimeDuration: string | null;
    sectionType: string;
    isMandatory?: boolean;
}

type SectionContentType = 'TEXT_SIMPLE' | 'TEXT_SELECTABLE' | 'QUESTION_SIMPLE' | 'QUESTION_MULTIPLE' | 'QUESTION_IMAGE' | 'CHALLENGE' | 'IMAGE_COMMENT';

interface SectionContentItem {
    id?: string;
    sectionContentId?: string;
    projectSectionContentId?: string;
    templateSectionContentId?: string | null;
    sectionId: string;
    name?: string;
    sectionName?: string;
    type?: SectionContentType;
    contentType?: SectionContentType;
    content: any;
    position: number;
    is_editable?: boolean;
    isEditable?: boolean;
    isIncluded?: boolean;
}

const ProjectDetail: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const [project, setProject] = useState<ProjectDetail | null>(null);
    const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState<string[]>([]);

    // Drag and drop states
    const [draggedGroupIndex, setDraggedGroupIndex] = useState<number | null>(null);
    const [draggedSectionIndex, setDraggedSectionIndex] = useState<number | null>(null);
    const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);
    const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(null);
    const [dragOverSectionIndex, setDragOverSectionIndex] = useState<number | null>(null);
    const [dragOverSectionGroupId, setDragOverSectionGroupId] = useState<string | null>(null);
    const [dragOverPosition, setDragOverPosition] = useState<'before' | 'after' | null>(null);
    const [dragOverFirstPosition, setDragOverFirstPosition] = useState<boolean>(false);
    const [dragOverLastPosition, setDragOverLastPosition] = useState<boolean>(false);
    const [dragOverFirstSectionGroup, setDragOverFirstSectionGroup] = useState<string | null>(null);
    const [dragOverLastSectionGroup, setDragOverLastSectionGroup] = useState<string | null>(null);
    const [isProcessingDrop, setIsProcessingDrop] = useState(false);
    const [lastProcessedDrop, setLastProcessedDrop] = useState<string | null>(null);

    // Form states
    const [isGroupFormOpen, setIsGroupFormOpen] = useState<boolean>(false);
    const [isSectionFormOpen, setIsSectionFormOpen] = useState<boolean>(false);
    const [editingGroup, setEditingGroup] = useState<ProjectGroup | null>(null);
    const [editingSection, setEditingSection] = useState<ProjectSection | null>(null);
    const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
    const [groupFormError, setGroupFormError] = useState<string>('');
    const [sectionFormError, setSectionFormError] = useState<string>('');
    const [groupFormData, setGroupFormData] = useState({
        title: '',
        subtitle: ''
    });
    const [sectionFormData, setSectionFormData] = useState({
        title: '',
        recommendationText: '',
        sectionType: 'TEXT',
        timeDuration: '',
        hours: '0',
        minutes: '0',
        summary: '',
        purpose: ''
    });

    // Section Content Management states
    const [isSectionContentManagerOpen, setIsSectionContentManagerOpen] = useState(false);
    const [editingSectionContentSection, setEditingSectionContentSection] = useState<ProjectSection | null>(null);
    const [sectionContentItems, setSectionContentItems] = useState<SectionContentItem[]>([]);
    const [isLoadingSectionContent, setIsLoadingSectionContent] = useState(false);
    const [sectionContentManagerError, setSectionContentManagerError] = useState<string>('');
    const [editingSectionContentItem, setEditingSectionContentItem] = useState<SectionContentItem | null>(null);
    const [isSectionContentFormOpen, setIsSectionContentFormOpen] = useState(false);
    const [sectionContentFormError, setSectionContentFormError] = useState<string>('');
    const [showContentTypeSelection, setShowContentTypeSelection] = useState(false);
    const [sectionContentFormData, setSectionContentFormData] = useState<{
        name: string;
        type: SectionContentType;
        content: any;
        is_editable: boolean;
    }>({
        name: '',
        type: 'TEXT_SIMPLE',
        content: '',
        is_editable: false
    });

    // Content viewer states for read-only content
    const [viewingContentItem, setViewingContentItem] = useState<SectionContentItem | null>(null);
    const [isViewingContent, setIsViewingContent] = useState(false);

    // Group submission modal states
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [groupToSubmit, setGroupToSubmit] = useState<ProjectGroup | null>(null);
    const [isIncludeModalOpen, setIsIncludeModalOpen] = useState(false);
    const [contentToInclude, setContentToInclude] = useState<SectionContentItem | null>(null);
    const [isEnableSectionModalOpen, setIsEnableSectionModalOpen] = useState(false);
    const [sectionToEnable, setSectionToEnable] = useState<ProjectSection | null>(null);
    const [isSubmittingGroup, setIsSubmittingGroup] = useState(false);
    const [submitError, setSubmitError] = useState<string>('');

    // Delivery modal states
    const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
    const [deliveryComments, setDeliveryComments] = useState<string>('');
    const [isSendingDelivery, setIsSendingDelivery] = useState(false);
    const [deliveryError, setDeliveryError] = useState<string>('');
    const [deliveredGroups, setDeliveredGroups] = useState<Set<string>>(new Set());

    // Fetch project details
    const fetchProject = useCallback(async () => {
        if (!projectId) return;

        setIsLoading(true);
        try {
            const response = await apiClient.get(`/quiz/project/${projectId}`);
            const data: ProjectDetailResponse = response.data;
            setProject(data.project);
            setProjectGroups(data.projectGroups);
        } catch (error: any) {
            console.error('Error fetching project details:', error);
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    // Fetch existing deliveries to populate deliveredGroups
    const fetchDeliveries = useCallback(async () => {
        if (!projectId) return;

        try {
            const response = await apiClient.get(`/quiz/project/${projectId}/deliveries`);
            const deliveries = response.data;
            const deliveredIds = new Set<string>(
                deliveries.map((d: any) => d.projectGroupId)
            );
            setDeliveredGroups(deliveredIds);
        } catch (error: any) {
            console.error('Error fetching deliveries:', error);
        }
    }, [projectId]);

    // Función para obtener el texto del tipo de proyecto
    const getProjectTypeText = (type: string): string => {
        switch (type) {
            case 'NEW_CONSTRUCTION':
                return 'Obra Nueva';
            case 'RENOVATION':
                return 'Reforma';
            default:
                return type || 'Desconocido';
        }
    };

    // Función para obtener el texto de la superficie
    const getSurfaceText = (surface: string): string => {
        switch (surface) {
            case 'AREA_LOWER_THAN_50':
                return '< 50m²';
            case 'AREA_BETWEEN_51_AND_100':
                return '51 - 100m²';
            case 'AREA_BETWEEN_101_AND_200':
                return '101 - 200m²';
            case 'AREA_BETWEEN_201_AND_500':
                return '201 - 500m²';
            case 'AREA_GREATER_THAN_500':
                return '> 500m²';
            default:
                return surface || 'Desconocido';
        }
    };

    // Formatear fecha
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        } catch (e) {
            return 'Fecha inválida';
        }
    };

    const toggleSectionExpansion = (sectionId: string) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    // Group submission helper functions
    const getActiveSections = useCallback((group: ProjectGroup): ProjectSection[] => {
        return group.projectSections.filter(section =>
            section.enabled === true || section.isMandatory === true
        );
    }, []);

    // Section readiness state - will be calculated locally without API calls
    const [sectionReadiness, setSectionReadiness] = useState<{ [sectionId: string]: boolean }>({});

    const isGroupReady = useCallback((group: ProjectGroup): boolean => {
        const activeSections = getActiveSections(group);
        // A group is always considered ready if it has active sections
        // The backend will validate the actual readiness when submitting
        return activeSections.length > 0;
    }, [getActiveSections]);

    const hasActiveSections = useCallback((group: ProjectGroup): boolean => {
        return getActiveSections(group).length > 0;
    }, [getActiveSections]);

    const isGroupSubmittable = useCallback((group: ProjectGroup): boolean => {
        if (group.submitted) return false;
        return isGroupReady(group) && hasActiveSections(group);
    }, [isGroupReady, hasActiveSections]);

    const getSubmitTooltipMessage = useCallback((group: ProjectGroup): string => {
        if (group.submitted) {
            return 'Este grupo ya ha sido enviado';
        }

        const activeSections = getActiveSections(group);
        if (activeSections.length === 0) {
            return 'No hay secciones activas en este grupo';
        }

        return '';
    }, [getActiveSections]);

    const openSubmitModal = useCallback((group: ProjectGroup) => {
        setGroupToSubmit(group);
        setIsSubmitModalOpen(true);
        setSubmitError('');
    }, []);

    const closeSubmitModal = useCallback(() => {
        setIsSubmitModalOpen(false);
        setGroupToSubmit(null);
        setSubmitError('');
    }, []);

    // Delivery functions
    const getPendingDeliveryGroups = useCallback((): ProjectGroup[] => {
        return projectGroups.filter(group =>
            group.submitted && !deliveredGroups.has(group.projectGroupId)
        );
    }, [projectGroups, deliveredGroups]);

    const openDeliveryModal = useCallback(() => {
        setIsDeliveryModalOpen(true);
        setDeliveryComments('');
        setDeliveryError('');
    }, []);

    const closeDeliveryModal = useCallback(() => {
        setIsDeliveryModalOpen(false);
        setDeliveryComments('');
        setDeliveryError('');
    }, []);

    const handleSendDeliveries = useCallback(async () => {
        if (!projectId) return;

        const pendingGroups = getPendingDeliveryGroups();
        if (pendingGroups.length === 0) return;

        setIsSendingDelivery(true);
        setDeliveryError('');

        try {
            // Send delivery for each pending group
            for (const group of pendingGroups) {
                await apiClient.post(`/quiz/project/${projectId}/deliveries`, {
                    project_group_id: group.projectGroupId,
                    type: 'GROUP_DELIVERY',
                    comments: deliveryComments || undefined
                });
            }

            // Mark groups as delivered
            setDeliveredGroups(prev => {
                const newSet = new Set(prev);
                pendingGroups.forEach(group => newSet.add(group.projectGroupId));
                return newSet;
            });

            // Close modal and show success
            closeDeliveryModal();
            alert(`Envío realizado correctamente para ${pendingGroups.length} grupo(s)`);
        } catch (error: any) {
            console.error('Error sending deliveries:', error);
            setDeliveryError(error.response?.data?.error || 'Error al enviar los deliveries');
        } finally {
            setIsSendingDelivery(false);
        }
    }, [projectId, getPendingDeliveryGroups, deliveryComments, closeDeliveryModal]);

    const closeIncludeModal = useCallback(() => {
        setIsIncludeModalOpen(false);
        setContentToInclude(null);
    }, []);

    const handleGroupSubmit = useCallback(async () => {
        if (!groupToSubmit || !projectId) return;

        setIsSubmittingGroup(true);
        setSubmitError('');

        try {
            await apiClient.post(
                `/quiz/project/${projectId}/group/${groupToSubmit.projectGroupId}/submit`
            );

            // Update local state
            setProjectGroups(prev =>
                prev.map(g =>
                    g.projectGroupId === groupToSubmit.projectGroupId
                        ? { ...g, submitted: true }
                        : g
                )
            );

            closeSubmitModal();
        } catch (error: any) {
            console.error('Error submitting group:', error);
            const errorMessage = error.response?.data?.error || 'Error al enviar el grupo. Por favor, inténtalo de nuevo.';
            setSubmitError(errorMessage);
        } finally {
            setIsSubmittingGroup(false);
        }
    }, [groupToSubmit, projectId, closeSubmitModal]);

    const handleToggleSubmit = useCallback((group: ProjectGroup, newValue: boolean) => {
        if (newValue && !group.submitted) {
            // Opening the toggle (trying to submit)
            openSubmitModal(group);
        }
        // If toggling off or already submitted, do nothing
    }, [openSubmitModal]);

    // Toggle section enabled/disabled
    const toggleSectionEnabled = async (section: ProjectSection) => {
        // Si es obligatoria, no permitir deshabilitar (se maneja en la UI con tooltip)
        if (section.isMandatory && section.enabled) {
            return;
        }

        // Si se va a habilitar, cargar contenidos y abrir modal de confirmación
        if (!section.enabled) {
            try {
                // Cargar contenidos de la sección para mostrar en el modal
                const response = await apiClient.get(`/quiz/project-sections/${section.projectSectionId}/contents`);
                let items = [];
                if (response.data.project_section_contents && Array.isArray(response.data.project_section_contents)) {
                    items = response.data.project_section_contents;
                } else if (Array.isArray(response.data)) {
                    items = response.data;
                }
                setSectionContentItems(items);
                setSectionToEnable(section);
                setIsEnableSectionModalOpen(true);
            } catch (error) {
                console.error('Error loading section contents:', error);
            }
        } else {
            // Si se va a deshabilitar, hacerlo directamente
            await confirmSectionEnable(section);
        }
    };

    // Confirmar habilitación de sección
    const confirmSectionEnable = async (section: ProjectSection) => {
        try {
            const newEnabledState = !section.enabled;

            await apiClient.put(`/quiz/project-sections/${section.projectSectionId}/enabled`, {
                enabled: newEnabledState
            });

            // Update local state
            setProjectGroups(prev =>
                prev.map(group => ({
                    ...group,
                    projectSections: group.projectSections.map(s =>
                        s.projectSectionId === section.projectSectionId
                            ? { ...s, enabled: newEnabledState }
                            : s
                    )
                }))
            );

            // Cerrar modal
            setIsEnableSectionModalOpen(false);
            setSectionToEnable(null);
        } catch (error) {
            console.error('Error toggling section enabled:', error);
        }
    };

    // Cerrar modal de habilitación de sección
    const closeSectionEnableModal = useCallback(() => {
        setIsEnableSectionModalOpen(false);
        setSectionToEnable(null);
    }, []);

    // Toggle section content included/excluded
    const toggleContentIncluded = (contentId: string, currentIncluded: boolean) => {
        // Si se va a incluir (activar), abrir modal de confirmación
        if (!currentIncluded) {
            const item = sectionContentItems.find(i => i.projectSectionContentId === contentId);
            if (item) {
                setContentToInclude(item);
                setIsIncludeModalOpen(true);
            }
        } else {
            // Si se va a excluir (desactivar), hacerlo directamente
            confirmContentInclusion(contentId, currentIncluded);
        }
    };

    // Confirmar inclusión de contenido
    const confirmContentInclusion = async (contentId: string, currentIncluded: boolean) => {
        try {
            const newIncludedState = !currentIncluded;

            // Mark as included/excluded
            await apiClient.put(`/quiz/project-section-contents/${contentId}/included`, {
                is_included: newIncludedState
            });

            // Update local state
            setSectionContentItems(prev =>
                prev.map(item =>
                    item.projectSectionContentId === contentId
                        ? { ...item, isIncluded: newIncludedState }
                        : item
                )
            );

            // Cerrar modal
            setIsIncludeModalOpen(false);
            setContentToInclude(null);
        } catch (error) {
            console.error('Error toggling content inclusion:', error);
        }
    };

    // Open Section Content Manager
    const openSectionContentManager = async (section: ProjectSection) => {
        setEditingSectionContentSection(section);
        setSectionContentManagerError('');
        setIsSectionContentManagerOpen(true);
        setIsLoadingSectionContent(true);

        try {
            // Use correct project-sections endpoint from documentation
            const response = await apiClient.get(`/quiz/project-sections/${section.projectSectionId}/contents`);
            console.log('Section content response:', response.data);

            // Handle the specific response format with project_section_contents
            let items = [];
            if (response.data.project_section_contents && Array.isArray(response.data.project_section_contents)) {
                items = response.data.project_section_contents;
            } else if (Array.isArray(response.data)) {
                items = response.data;
            }

            // Show all items but mark which ones are editable
            setSectionContentItems(items);
        } catch (error: any) {
            console.error('Error loading section content:', error);
            setSectionContentManagerError('Error al cargar el contenido de la sección');
        } finally {
            setIsLoadingSectionContent(false);
        }
    };

    // Content viewer functions for read-only content
    const openContentViewer = (item: SectionContentItem) => {
        setViewingContentItem(item);
        setIsViewingContent(true);
    };

    const closeContentViewer = () => {
        setIsViewingContent(false);
        setViewingContentItem(null);
    };

    // Close Section Content Manager
    const closeSectionContentManager = () => {
        setIsSectionContentManagerOpen(false);
        setEditingSectionContentSection(null);
        setSectionContentItems([]);
        setSectionContentManagerError('');
        // Reset form if open
        if (isSectionContentFormOpen) {
            setIsSectionContentFormOpen(false);
            setEditingSectionContentItem(null);
            setSectionContentFormData({
                name: '',
                type: 'TEXT_SIMPLE',
                content: '',
                is_editable: false
            });
        }
    };

    const isCustomContent = (item: SectionContentItem) => !item.templateSectionContentId;

    const moveContent = async (index: number, direction: 'up' | 'down') => {
        if (!editingSectionContentSection) return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= sectionContentItems.length) return;

        // Swap in local array
        const items = [...sectionContentItems];
        [items[index], items[targetIndex]] = [items[targetIndex], items[index]];

        // Assign sequential positions based on new order
        const updates = items
            .map((item, i) => ({ item, newPosition: i }))
            .filter(({ item, newPosition }) => (item.position ?? 0) !== newPosition);

        try {
            // Persist changed positions
            for (const { item, newPosition } of updates) {
                await apiClient.put(`/quiz/project-section-contents/${item.projectSectionContentId}`, {
                    content: item.content,
                    position: newPosition,
                });
            }

            // Refresh list
            const listResponse = await apiClient.get(`/quiz/project-sections/${editingSectionContentSection.projectSectionId}/contents`);
            let refreshedItems: SectionContentItem[] = [];
            if (listResponse.data.project_section_contents && Array.isArray(listResponse.data.project_section_contents)) {
                refreshedItems = listResponse.data.project_section_contents;
            } else if (Array.isArray(listResponse.data)) {
                refreshedItems = listResponse.data;
            }
            setSectionContentItems(refreshedItems);
        } catch (error) {
            console.error('Error moving content:', error);
        }
    };

    // Get content type info
    const getContentTypeInfo = (type: SectionContentType) => {
        const typeInfo = {
            TEXT_SIMPLE: { icon: 'bi-justify', name: 'Texto Simple' },
            TEXT_SELECTABLE: { icon: 'bi-list-check', name: 'Texto Seleccionable' },
            QUESTION_SIMPLE: { icon: 'bi-patch-question', name: 'Pregunta Simple' },
            QUESTION_MULTIPLE: { icon: 'bi-ui-checks', name: 'Pregunta Múltiple' },
            QUESTION_IMAGE: { icon: 'bi-image', name: 'Pregunta con Imagen' },
            CHALLENGE: { icon: 'bi-puzzle', name: 'Desafío' },
            IMAGE_COMMENT: { icon: 'bi-image', name: 'Imagen + Comentario' }
        };
        return typeInfo[type] || { icon: 'bi-file-text', name: 'Contenido' };
    };

    // Get content preview (first 120 characters)
    const getContentPreview = (item: SectionContentItem): string => {
        try {
            const itemType = item.contentType || item.type;
            let text = '';

            if (itemType === 'QUESTION_MULTIPLE') {
                if (item.content?.questionText && typeof item.content.questionText === 'object') {
                    text = item.content.questionText.singular || item.content.questionText.original || '';
                } else if (typeof item.content?.questionText === 'string') {
                    text = item.content.questionText;
                } else if (item.content?.text) {
                    text = item.content.text;
                }
            } else if (itemType === 'QUESTION_SIMPLE') {
                if (item.content && typeof item.content === 'object') {
                    text = item.content.singular || item.content.original || '';
                } else if (typeof item.content === 'string') {
                    text = item.content;
                }
            } else if (itemType === 'TEXT_SIMPLE' || itemType === 'TEXT_SELECTABLE') {
                // Handle object with singular/plural
                if (item.content && typeof item.content === 'object') {
                    text = item.content.singular || item.content.plural || item.content.original || '';
                } else if (typeof item.content === 'string') {
                    text = item.content;
                }
            } else if (typeof item.content === 'string') {
                text = item.content;
            } else if (item.content && typeof item.content === 'object') {
                // Fallback for any object with singular/plural
                text = item.content.singular || item.content.plural || item.content.original || '';
            }

            if (!text) return '';

            // Quita tags y decodifica entidades HTML (&nbsp;, etc.) para que la
            // previsualización no muestre entidades en crudo (bug #12).
            return htmlToPreviewText(text, 120);
        } catch (error) {
            console.error('Error getting content preview:', error);
            return '';
        }
    };

    // Get full content text (for modal display)
    const getFullContentText = (item: SectionContentItem): string => {
        try {
            const itemType = item.contentType || item.type;
            let text = '';

            if (itemType === 'QUESTION_SIMPLE') {
                if (item.content && typeof item.content === 'object') {
                    text = item.content.singular || item.content.original || '';
                } else if (typeof item.content === 'string') {
                    text = item.content;
                }
            } else if (itemType === 'TEXT_SIMPLE') {
                if (item.content && typeof item.content === 'object') {
                    text = item.content.singular || item.content.plural || item.content.original || '';
                } else if (typeof item.content === 'string') {
                    text = item.content;
                }
            } else if (itemType === 'QUESTION_MULTIPLE') {
                // For QUESTION_MULTIPLE, show only the question text
                if (item.content?.questionText && typeof item.content.questionText === 'object') {
                    text = item.content.questionText.singular || item.content.questionText.original || '';
                } else if (typeof item.content?.questionText === 'string') {
                    text = item.content.questionText;
                } else if (item.content?.text) {
                    text = item.content.text;
                }
            }

            return text;
        } catch (error) {
            console.error('Error getting full content text:', error);
            return '';
        }
    };

    // Open section content form
    const openSectionContentForm = (item?: SectionContentItem) => {
        setSectionContentFormError('');
        if (item) {
            setEditingSectionContentItem(item);
            setSectionContentFormData({
                name: item.sectionName || item.name || '',
                type: item.contentType || item.type || 'TEXT_SIMPLE',
                content: item.content || '',
                is_editable: item.isEditable !== undefined ? item.isEditable : (item.is_editable !== undefined ? item.is_editable : false)
            });
        } else {
            setEditingSectionContentItem(null);
            setSectionContentFormData({
                name: '',
                type: 'TEXT_SIMPLE',
                content: '',
                is_editable: false
            });
        }
        setIsSectionContentFormOpen(true);
        setShowContentTypeSelection(false);
    };

    // Save section content item
    const saveSectionContentItem = async () => {
        if (!editingSectionContentSection) return;

        setSectionContentFormError('');

        try {
            let contentData: any = {};

            // Format content as JSON object based on type
            if (sectionContentFormData.type === 'TEXT_SIMPLE' || sectionContentFormData.type === 'TEXT_SELECTABLE' || sectionContentFormData.type === 'QUESTION_SIMPLE') {
                contentData = sectionContentFormData.content;
            } else if (sectionContentFormData.type === 'QUESTION_MULTIPLE') {
                // content already holds the full editor payload ({ questionText, questionImage, answers, allowMultiple })
                contentData = (sectionContentFormData.content && typeof sectionContentFormData.content === 'object')
                    ? sectionContentFormData.content
                    : { questionText: sectionContentFormData.content || '', answers: [], allowMultiple: false };
            } else if (sectionContentFormData.type === 'QUESTION_IMAGE') {
                contentData = {
                    text: sectionContentFormData.content,
                    imageUrl: '',
                    imageFilename: ''
                };
            } else if (sectionContentFormData.type === 'IMAGE_COMMENT') {
                contentData = {
                    imageUrl: '',
                    comment: sectionContentFormData.content || ''
                };
            } else {
                contentData = sectionContentFormData.content;
            }

            if (editingSectionContentItem?.projectSectionContentId) {
                // UPDATE existing content
                const itemData = {
                    name: sectionContentFormData.name,
                    type: sectionContentFormData.type,
                    content: contentData,
                    is_editable: sectionContentFormData.is_editable
                };

                await apiClient.put(
                    `/quiz/project-section-contents/${editingSectionContentItem.projectSectionContentId}`,
                    itemData
                );
            } else {
                // CREATE new content
                if (!sectionContentFormData.name.trim()) {
                    setSectionContentFormError('El nombre es obligatorio');
                    return;
                }

                const createResponse = await apiClient.post('/quiz/project-section-contents', {
                    project_section_id: editingSectionContentSection.projectSectionId,
                    content_type: sectionContentFormData.type,
                    section_name: sectionContentFormData.name,
                    content: typeof contentData === 'string' ? { text: contentData } : contentData,
                    is_ready: true,
                    position: sectionContentItems.length,
                });
                console.log('Content created:', createResponse.data);
                // Small delay to allow the read model to catch up (CQRS eventual consistency)
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Refresh the content list
            const listResponse = await apiClient.get(`/quiz/project-sections/${editingSectionContentSection.projectSectionId}/contents`);

            let items = [];
            if (listResponse.data.project_section_contents && Array.isArray(listResponse.data.project_section_contents)) {
                items = listResponse.data.project_section_contents;
            } else if (Array.isArray(listResponse.data)) {
                items = listResponse.data;
            }

            console.log('Refresh response:', listResponse.data);
            console.log('Items to display:', items);
            setSectionContentItems(items);

            // Close the form
            setIsSectionContentFormOpen(false);
            setEditingSectionContentItem(null);
        } catch (error: any) {
            console.error('Error saving content:', error);
            const errMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al guardar el contenido';
            setSectionContentFormError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
        }
    };

    // Group form handlers
    const toggleGroupForm = () => {
        setIsGroupFormOpen(!isGroupFormOpen);
        if (!isGroupFormOpen) {
            // Reset form when opening
            setGroupFormData({
                title: '',
                subtitle: ''
            });
            setEditingGroup(null);
            setGroupFormError('');
        } else {
            // Reset editing state when closing
            setEditingGroup(null);
            setGroupFormError('');
        }
    };

    const handleGroupInputChange = (field: string, value: string) => {
        setGroupFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleGroupFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setGroupFormError('');

        if (!projectId) return;

        const payload = {
            name: groupFormData.title,
            subtitle: groupFormData.subtitle || null
        };

        try {
            await apiClient.post(`/quiz/project/${projectId}/group`, payload);
            toggleGroupForm();
            fetchProject(); // Refresh the data
        } catch (error: any) {
            console.error('Error creating project group:', error);

            let errorMessage = 'Ha ocurrido un error al crear el grupo. Por favor, inténtalo de nuevo.';

            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data.error === 'string') {
                    errorMessage = data.error;
                } else if (typeof data.error === 'object' && data.error.message) {
                    errorMessage = data.error.message;
                } else if (typeof data.message === 'string') {
                    errorMessage = data.message;
                } else if (typeof data.message === 'object' && data.message.message) {
                    errorMessage = data.message.message;
                } else if (typeof data.detail === 'string') {
                    errorMessage = data.detail;
                } else if (typeof data.detail === 'object' && data.detail.message) {
                    errorMessage = data.detail.message;
                }
            }

            setGroupFormError(errorMessage);
        }
    };

    // Section form handlers
    const toggleSectionForm = () => {
        setIsSectionFormOpen(!isSectionFormOpen);
        if (!isSectionFormOpen) {
            setSectionFormData({
                title: '',
                recommendationText: '',
                sectionType: 'TEXT',
                timeDuration: '',
                hours: '0',
                minutes: '0',
                summary: '',
                purpose: ''
            });
            setEditingSection(null);
            setCurrentGroupId(null);
            setSectionFormError('');
        } else {
            setEditingSection(null);
            setCurrentGroupId(null);
            setSectionFormError('');
        }
    };

    const openCreateSectionForm = (groupId: string) => {
        setCurrentGroupId(groupId);
        setEditingSection(null);
        setSectionFormData({
            title: '',
            recommendationText: '',
            sectionType: 'TEXT',
            timeDuration: '',
            hours: '0',
            minutes: '0',
            summary: '',
            purpose: ''
        });
        setSectionFormError('');
        setIsSectionFormOpen(true);
    };

    const handleSectionInputChange = (field: string, value: string) => {
        setSectionFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSectionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSectionFormError('');

        if (!projectId || !currentGroupId) return;

        // Convert hours and minutes to a time string
        const hours = parseInt(sectionFormData.hours) || 0;
        const minutes = parseInt(sectionFormData.minutes) || 0;
        const timeString = hours > 0 ?
            `${hours} hora${hours > 1 ? 's' : ''} ${minutes > 0 ? `${minutes} minutos` : ''}`.trim() :
            minutes > 0 ? `${minutes} minutos` : null;

        const payload = {
            section_name: sectionFormData.title,
            section_type: sectionFormData.sectionType,
            position: 0,
            enabled: true,
            labels: [],
            recommendation_text: sectionFormData.recommendationText || null,
            featured: false,
            section_summary: sectionFormData.summary || null,
            section_purpose: sectionFormData.purpose || null,
            section_time_duration: timeString
        };

        try {
            await apiClient.post(`/quiz/project/${projectId}/group/${currentGroupId}/section`, payload);
            toggleSectionForm();
            fetchProject(); // Refresh the data
        } catch (error: any) {
            console.error('Error creating project section:', error);

            let errorMessage = 'Ha ocurrido un error al crear la sección. Por favor, inténtalo de nuevo.';

            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data.error === 'string') {
                    errorMessage = data.error;
                } else if (typeof data.error === 'object' && data.error.message) {
                    errorMessage = data.error.message;
                } else if (typeof data.message === 'string') {
                    errorMessage = data.message;
                } else if (typeof data.message === 'object' && data.message.message) {
                    errorMessage = data.message.message;
                } else if (typeof data.detail === 'string') {
                    errorMessage = data.detail;
                } else if (typeof data.detail === 'object' && data.detail.message) {
                    errorMessage = data.detail.message;
                }
            }

            setSectionFormError(errorMessage);
        }
    };

    // Calculate total time duration for a group
    const calculateGroupTimeDuration = (sections: ProjectSection[]) => {
        if (!sections || sections.length === 0) return null;

        let totalHours = 0;
        let totalMinutes = 0;

        sections.forEach((section: ProjectSection) => {
            if (section.sectionTimeDuration) {
                const timeString = section.sectionTimeDuration;
                const hoursMatch = timeString.match(/(\d+)\s*hora?s?/);
                const minutesMatch = timeString.match(/(\d+)\s*minutos?/);

                if (hoursMatch) {
                    totalHours += parseInt(hoursMatch[1]);
                }
                if (minutesMatch) {
                    totalMinutes += parseInt(minutesMatch[1]);
                }
            }
        });

        // Convert excess minutes to hours
        totalHours += Math.floor(totalMinutes / 60);
        totalMinutes = totalMinutes % 60;

        // Format the result
        if (totalHours > 0 && totalMinutes > 0) {
            return `${totalHours} hora${totalHours > 1 ? 's' : ''} ${totalMinutes} minutos`;
        } else if (totalHours > 0) {
            return `${totalHours} hora${totalHours > 1 ? 's' : ''}`;
        } else if (totalMinutes > 0) {
            return `${totalMinutes} minutos`;
        }

        return null;
    };

    // Drag and drop handlers for groups
    const handleGroupDragStart = (e: React.DragEvent, groupIndex: number) => {
        setDraggedGroupIndex(groupIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleGroupDragEnd = () => {
        setTimeout(() => {
            setDraggedGroupIndex(null);
            setDragOverGroupIndex(null);
            setDragOverPosition(null);
            setDragOverFirstPosition(false);
            setDragOverLastPosition(false);
        }, 0);
    };

    const handleGroupDragOver = (e: React.DragEvent, overIndex: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedGroupIndex !== null && draggedGroupIndex !== overIndex) {
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY;
            const midPoint = rect.top + (rect.height / 2);

            setDragOverGroupIndex(overIndex);
            setDragOverPosition(y < midPoint ? 'before' : 'after');
        }
    };

    const handleGroupDrop = async (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedGroupIndex === null) return;

        const newGroups = [...projectGroups];
        const draggedGroup = newGroups[draggedGroupIndex];

        let actualDropIndex = dropIndex;
        if (dragOverPosition === 'after') {
            actualDropIndex = dropIndex + 1;
        }

        if (draggedGroupIndex < actualDropIndex) {
            actualDropIndex--;
        }

        if (draggedGroupIndex === actualDropIndex) return;

        newGroups.splice(draggedGroupIndex, 1);
        newGroups.splice(actualDropIndex, 0, draggedGroup);

        setProjectGroups(newGroups);
        setDraggedGroupIndex(null);
        setDragOverGroupIndex(null);
        setDragOverPosition(null);

        // Update positions in backend
        try {
            const updatePromises = newGroups.map((group, index) =>
                apiClient.put(`/quiz/project-group/${group.projectGroupId}/position`, {
                    position: index
                })
            );
            await Promise.all(updatePromises);
        } catch (error) {
            console.error('Error updating group positions:', error);
            fetchProject(); // Revert on error
        }
    };

    // Drag and drop handlers for sections
    const handleSectionDragStart = (e: React.DragEvent, sectionIndex: number, groupId: string) => {
        setDraggedSectionIndex(sectionIndex);
        setDraggedGroupId(groupId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleSectionDragEnd = () => {
        setTimeout(() => {
            setDraggedSectionIndex(null);
            setDraggedGroupId(null);
            setDragOverSectionIndex(null);
            setDragOverSectionGroupId(null);
            setDragOverPosition(null);
            setDragOverFirstSectionGroup(null);
            setDragOverLastSectionGroup(null);
            setIsProcessingDrop(false);
            setLastProcessedDrop(null);
        }, 0);
    };

    const handleSectionDragOver = (e: React.DragEvent, overIndex: number, groupId: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedSectionIndex !== null && (draggedGroupId !== groupId || draggedSectionIndex !== overIndex)) {
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY;
            const midPoint = rect.top + (rect.height / 2);
            const position = y < midPoint ? 'before' : 'after';

            setDragOverSectionIndex(overIndex);
            setDragOverSectionGroupId(groupId);
            setDragOverPosition(position);
        }
    };

    const createDropId = (sectionIndex: number, groupId: string, targetIndex: number, targetGroupId: string) => {
        return `${sectionIndex}-${groupId}-${targetIndex}-${targetGroupId}-${Date.now()}`;
    };

    const handleSectionDrop = async (e: React.DragEvent, dropIndex: number, groupId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const currentDraggedSectionIndex = draggedSectionIndex;
        const currentDraggedGroupId = draggedGroupId;

        if (currentDraggedSectionIndex === null || currentDraggedGroupId === null) return;

        const dropId = createDropId(currentDraggedSectionIndex, currentDraggedGroupId, dropIndex, groupId);

        if (isProcessingDrop || lastProcessedDrop === dropId) return;

        setIsProcessingDrop(true);
        setLastProcessedDrop(dropId);

        setTimeout(() => setIsProcessingDrop(false), 200);

        setDraggedSectionIndex(null);
        setDraggedGroupId(null);
        setDragOverSectionIndex(null);
        setDragOverSectionGroupId(null);
        setDragOverPosition(null);

        const newGroups = [...projectGroups];
        const sourceGroupIndex = newGroups.findIndex(g => g.projectGroupId === currentDraggedGroupId);
        const targetGroupIndex = newGroups.findIndex(g => g.projectGroupId === groupId);

        if (sourceGroupIndex === -1 || targetGroupIndex === -1) return;

        const draggedSection = newGroups[sourceGroupIndex].projectSections[currentDraggedSectionIndex];

        let actualDropIndex = dropIndex;
        if (dragOverPosition === 'after') {
            actualDropIndex = dropIndex + 1;
        }

        if (currentDraggedGroupId === groupId) {
            if (currentDraggedSectionIndex < actualDropIndex) {
                actualDropIndex--;
            }
            if (currentDraggedSectionIndex === actualDropIndex) return;
        }

        newGroups[sourceGroupIndex].projectSections.splice(currentDraggedSectionIndex, 1);
        newGroups[targetGroupIndex].projectSections.splice(actualDropIndex, 0, draggedSection);

        setProjectGroups(newGroups);

        // Update positions in backend
        try {
            const updatePromises: Promise<any>[] = [];

            if (currentDraggedGroupId !== groupId) {
                newGroups[sourceGroupIndex].projectSections.forEach((section, index) => {
                    updatePromises.push(
                        apiClient.put(`/quiz/project-section/${section.projectSectionId}/position`, {
                            position: index
                        })
                    );
                });
            }

            const movedToAnotherGroup = currentDraggedGroupId !== groupId;
            newGroups[targetGroupIndex].projectSections.forEach((section, index) => {
                // La sección arrastrada cambió de grupo: reasignar su grupo en backend
                // además de la posición. Sin esto, la sección seguía en el grupo origen
                // en BD y el envío origen quedaba "colgado" al recargar (bug #13).
                const isMovedSection = section.projectSectionId === draggedSection.projectSectionId;
                const payload: { position: number; target_project_group_id?: string } = { position: index };
                if (movedToAnotherGroup && isMovedSection) {
                    payload.target_project_group_id = groupId;
                }
                updatePromises.push(
                    apiClient.put(`/quiz/project-section/${section.projectSectionId}/position`, payload)
                );
            });

            await Promise.all(updatePromises);
        } catch (error) {
            console.error('Error updating section positions:', error);
            fetchProject();
        }
    };

    // Fetch project on component mount
    useEffect(() => {
        fetchProject();
        fetchDeliveries();
    }, [fetchProject, fetchDeliveries]);

    if (isLoading) {
        return (
            <div className="container-custom py-4">
                <div className="text-center mt-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-3">Cargando detalles del proyecto...</p>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="container-custom py-4">
                <div className="row mb-4">
                    <div className="col">
                        <Link to="/architect" className="btn btn-outline-secondary mb-3">
                            <i className="bi bi-arrow-left me-2"></i>
                            Volver al Dashboard
                        </Link>
                        <h1 className="h3 mb-0">Proyecto no encontrado</h1>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container-custom py-4">
                <div className="row mb-4">
                    <div className="col d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <Link to="/architect" className="text-decoration-none me-4" style={{ color: 'inherit' }}>
                                <div className="d-flex align-items-center" style={{ fontSize: '2rem', color: 'var(--color-dark)' }}>
                                    <i className="bi bi-chevron-double-left"></i>
                                </div>
                            </Link>
                            <div>
                                <h1 className="h3 mb-0">{project.externalName}</h1>
                                <p className="text-muted mb-2">
                                    {getProjectTypeText(project.interventionType)}&nbsp;&nbsp;|&nbsp;&nbsp;{getSurfaceText(project.surface)}&nbsp;&nbsp;|&nbsp;&nbsp;Inicio: {formatDate(project.createdAt)}&nbsp;&nbsp;|&nbsp;&nbsp;Actualización: {formatDate(project.updatedAt)}
                                </p>
                            </div>
                        </div>
                        <Link to={`/architect/project/${projectId}/report`} className="btn btn-outline-dark">
                            <i className="bi bi-file-text me-2"></i>
                            Ver Reporte
                        </Link>
                    </div>
                </div>

                {/* Banner de envíos pendientes */}
                {(() => {
                    const pendingGroups = getPendingDeliveryGroups();
                    if (pendingGroups.length === 0) return null;

                    return (
                        <div className="row mt-3">
                            <div className="col-12">
                                <div className="alert alert-warning d-flex justify-content-between align-items-center mb-0" role="alert">
                                    <div>
                                        <i className="bi bi-exclamation-circle me-2"></i>
                                        <strong>Tienes {pendingGroups.length} envío{pendingGroups.length > 1 ? 's' : ''} pendiente{pendingGroups.length > 1 ? 's' : ''}</strong>
                                    </div>
                                    <button
                                        className="btn"
                                        onClick={openDeliveryModal}
                                        style={{
                                            borderRadius: 0,
                                            backgroundColor: '#ff6b35',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '0.5rem 1.5rem',
                                            fontSize: '1rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        Enviar
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                <div className="row mt-4">
                    <div className="col-12">
                        {(!projectGroups || !Array.isArray(projectGroups) || projectGroups.length === 0) ? (
                            <p className="text-muted">No hay grupos configurados para este proyecto</p>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {projectGroups.map((group: ProjectGroup, index: number) => {
                                    const hasChallenge = group.projectSections?.some(
                                        (section: ProjectSection) => section.sectionType === 'CHALLENGE'
                                    );
                                    return (
                                    <div
                                        key={group.projectGroupId || index}
                                        className={`quiz-group-card-full ${hasChallenge ? 'has-challenge' : ''} ${draggedGroupIndex === index ? 'dragging' : ''} ${dragOverGroupIndex === index && draggedGroupIndex !== null && draggedGroupIndex !== index
                                                ? `drag-over drag-${dragOverPosition}` : ''
                                            }`}
                                        draggable
                                        onDragStart={(e) => handleGroupDragStart(e, index)}
                                        onDragEnd={handleGroupDragEnd}
                                        onDragOver={(e) => handleGroupDragOver(e, index)}
                                        onDrop={(e) => handleGroupDrop(e, index)}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h6 className="quiz-group-title">{group.groupName || 'Sin título'}</h6>
                                                {(() => {
                                                    const calculatedTime = calculateGroupTimeDuration(group.projectSections);
                                                    if (group.groupSubtitle || calculatedTime) {
                                                        return (
                                                            <p className="quiz-group-subtitle">
                                                                {group.groupSubtitle && calculatedTime
                                                                    ? `${group.groupSubtitle}   |   Tiempo total aproximado ${calculatedTime}`
                                                                    : group.groupSubtitle
                                                                        ? group.groupSubtitle
                                                                        : calculatedTime
                                                                            ? `Tiempo total aproximado ${calculatedTime}`
                                                                            : ''
                                                                }
                                                            </p>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>

                                            {/* Toggle Enviar */}
                                            <div
                                                className="d-flex align-items-center gap-2"
                                                style={{ marginLeft: '1rem', position: 'relative' }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: '1rem',
                                                        color: group.submitted ? '#28a745' : 'var(--color-dark)',
                                                        fontWeight: group.submitted ? '500' : '400'
                                                    }}
                                                >
                                                    {group.submitted ? 'Enviado' : 'Listo para enviar'}
                                                </span>
                                                <div
                                                    className="form-check form-switch mb-0"
                                                    style={{ position: 'relative' }}
                                                    onClick={(e) => {
                                                        if (!isGroupSubmittable(group)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isGroupSubmittable(group)) {
                                                            const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                                                            if (tooltip) tooltip.style.display = 'block';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                                                        if (tooltip) tooltip.style.display = 'none';
                                                    }}
                                                >
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={group.submitted}
                                                        disabled={!isGroupSubmittable(group)}
                                                        onChange={(e) => handleToggleSubmit(group, e.target.checked)}
                                                        style={{
                                                            cursor: isGroupSubmittable(group) ? 'pointer' : 'not-allowed',
                                                            width: '50px',
                                                            height: '25px',
                                                            backgroundColor: group.submitted ? '#28a745' : undefined,
                                                            borderColor: group.submitted ? '#28a745' : undefined
                                                        }}
                                                    />
                                                    {!isGroupSubmittable(group) && (
                                                        <div
                                                            className="custom-tooltip"
                                                            style={{
                                                                display: 'none',
                                                                position: 'absolute',
                                                                bottom: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                marginBottom: '8px',
                                                                padding: '6px 12px',
                                                                backgroundColor: '#000',
                                                                color: '#fff',
                                                                fontSize: '0.875rem',
                                                                borderRadius: '4px',
                                                                whiteSpace: 'nowrap',
                                                                zIndex: 1000,
                                                                pointerEvents: 'none'
                                                            }}
                                                        >
                                                            {getSubmitTooltipMessage(group)}
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '6px solid transparent',
                                                                    borderRight: '6px solid transparent',
                                                                    borderTop: '6px solid #000'
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Display sections */}
                                        {group.projectSections && group.projectSections.length > 0 && (
                                            <div className={`sections-container mt-3 mb-3 ${draggedSectionIndex !== null && draggedGroupId === group.projectGroupId ? 'section-dragging' : ''}`}>
                                                {group.projectSections
                                                    .filter((section: ProjectSection) => !group.submitted || section.enabled)
                                                    .map((section: ProjectSection, sectionIndex: number) => (
                                                    <div
                                                        key={section.projectSectionId}
                                                        className={`section-card mb-2 ${draggedSectionIndex === sectionIndex && draggedGroupId === group.projectGroupId ? 'dragging' : ''
                                                            } ${dragOverSectionIndex === sectionIndex &&
                                                                dragOverSectionGroupId === group.projectGroupId &&
                                                                draggedSectionIndex !== null &&
                                                                (draggedGroupId !== group.projectGroupId || draggedSectionIndex !== sectionIndex) ? `drag-over drag-${dragOverPosition}` : ''
                                                            }`}
                                                        draggable
                                                        onDragStart={(e) => handleSectionDragStart(e, sectionIndex, group.projectGroupId)}
                                                        onDragEnd={handleSectionDragEnd}
                                                        onDragOver={(e) => handleSectionDragOver(e, sectionIndex, group.projectGroupId)}
                                                        onDrop={(e) => handleSectionDrop(e, sectionIndex, group.projectGroupId)}
                                                    >
                                                        <div
                                                            className="section-header d-flex justify-content-between align-items-center"
                                                            onClick={() => toggleSectionExpansion(section.projectSectionId)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                {/* Section Enabled Toggle */}
                                                                <div
                                                                    className="form-check form-switch me-2"
                                                                    style={{ margin: 0, opacity: 1 }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        role="switch"
                                                                        checked={section.enabled}
                                                                        disabled={group.submitted || (section.isMandatory && section.enabled)}
                                                                        onChange={() => toggleSectionEnabled(section)}
                                                                        title={
                                                                            group.submitted
                                                                                ? 'No se puede modificar, el grupo ya está enviado'
                                                                                : section.isMandatory && section.enabled
                                                                                ? 'Sección obligatoria, no se puede deshabilitar'
                                                                                : section.enabled
                                                                                ? 'Sección habilitada'
                                                                                : 'Sección deshabilitada'
                                                                        }
                                                                        style={{
                                                                            width: '40px',
                                                                            height: '20px',
                                                                            backgroundColor: section.enabled ? '#28a745' : undefined,
                                                                            borderColor: section.enabled ? '#28a745' : undefined,
                                                                            cursor: (group.submitted || (section.isMandatory && section.enabled)) ? 'not-allowed' : 'pointer'
                                                                        }}
                                                                    />
                                                                </div>

                                                                <i className={`bi ${section.sectionType === 'TEXT' ? 'bi-justify' :
                                                                        section.sectionType === 'QUESTION' ? 'bi-question-lg' :
                                                                            section.sectionType === 'CHALLENGE' ? 'bi-puzzle' :
                                                                                'bi-justify'
                                                                    } me-2`} style={{ opacity: section.enabled ? 1 : 0.5 }}></i>
                                                                <h6 className="section-title mb-0" style={{ opacity: section.enabled ? 1 : 0.5 }}>{section.sectionName}</h6>
                                                                {section.featured && (
                                                                    <span
                                                                        className="featured-icon ms-2"
                                                                        onMouseEnter={(e) => {
                                                                            const tooltip = document.createElement('div');
                                                                            tooltip.className = 'custom-tooltip';
                                                                            tooltip.textContent = 'Ejercicio destacado. Este ejercicio gusta mucho y da mucha info';
                                                                            document.body.appendChild(tooltip);

                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                                                            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                                                                            // Position tooltip above the icon, centered horizontally
                                                                            tooltip.style.left = rect.left + scrollLeft + (rect.width / 2) - (tooltip.offsetWidth / 2) + 'px';
                                                                            tooltip.style.top = rect.top + scrollTop - tooltip.offsetHeight - 8 + 'px';

                                                                            e.currentTarget.setAttribute('data-tooltip-id', Date.now().toString());
                                                                        }}
                                                                        onMouseLeave={() => {
                                                                            const tooltips = document.querySelectorAll('.custom-tooltip');
                                                                            tooltips.forEach(tooltip => tooltip.remove());
                                                                        }}
                                                                    >
                                                                        ☻
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3">
                                                                {section.sectionTimeDuration && (
                                                                    <span className="section-time text-muted">{section.sectionTimeDuration}</span>
                                                                )}
                                                                <button
                                                                    className="btn btn-link p-0 border-0"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (!group.submitted) {
                                                                            openSectionContentManager(section);
                                                                        }
                                                                    }}
                                                                    disabled={group.submitted}
                                                                    style={{
                                                                        fontSize: '0.875rem',
                                                                        textDecoration: 'none',
                                                                        color: '#000',
                                                                        opacity: group.submitted ? 0.5 : 1,
                                                                        cursor: group.submitted ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                >
                                                                    Editar
                                                                </button>
                                                                <i className={`bi ${expandedSections.includes(section.projectSectionId) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                            </div>
                                                        </div>

                                                        {expandedSections.includes(section.projectSectionId) && (
                                                            <div className="section-content mt-3 pt-3 border-top">
                                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                                    <div className="flex-grow-1">
                                                                        {section.recommendationText && (
                                                                            <div style={{ fontStyle: 'italic' }}>{section.recommendationText}</div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="row mb-3">
                                                                    {section.sectionSummary && (
                                                                        <div className="col-md-6">
                                                                            <div className="mb-2">
                                                                                <strong>¿En qué consiste?</strong>
                                                                            </div>
                                                                            <div>{section.sectionSummary}</div>
                                                                        </div>
                                                                    )}
                                                                    {section.sectionPurpose && (
                                                                        <div className="col-md-6">
                                                                            <div className="mb-2">
                                                                                <strong>¿Para qué sirve?</strong>
                                                                            </div>
                                                                            <div>{section.sectionPurpose}</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Create section button */}
                                        {!group.submitted && (
                                            <div className="mt-3">
                                                <div
                                                    className="create-section-box"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openCreateSectionForm(group.projectGroupId);
                                                    }}
                                                >
                                                    +
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create new group button */}
            <div className="row mt-4">
                <div className="col-12">
                    <div
                        className="create-submission-box"
                        onClick={toggleGroupForm}
                    >
                        +&nbsp;&nbsp;&nbsp;Crear nuevo grupo
                    </div>
                </div>
            </div>

            {/* Group Form Sidebar */}
            <div className={`submission-form-sidebar ${isGroupFormOpen ? 'open' : ''}`}>
                <button
                    className="close-form-btn"
                    onClick={toggleGroupForm}
                    aria-label="Cerrar formulario"
                >
                    ×
                </button>

                <div className="form-content">
                    <h4 className="mb-4">Nuevo Grupo</h4>

                    {groupFormError && (
                        <div className="alert alert-danger" role="alert">
                            {groupFormError}
                        </div>
                    )}

                    <form onSubmit={handleGroupFormSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Título del grupo</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ingrese el título"
                                value={groupFormData.title}
                                onChange={(e) => handleGroupInputChange('title', e.target.value)}
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Subtítulo</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: Antes del anteproyecto"
                                value={groupFormData.subtitle}
                                onChange={(e) => handleGroupInputChange('subtitle', e.target.value)}
                            />
                        </div>

                        <div className="d-flex justify-content-between mt-4">
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-cancel-custom" onClick={toggleGroupForm}>Cancelar</button>
                                <button type="submit" className="btn btn-create-custom">Crear</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {isGroupFormOpen && <div className="form-overlay" onClick={toggleGroupForm}></div>}

            {/* Section Form Sidebar */}
            <div className={`submission-form-sidebar ${isSectionFormOpen ? 'open' : ''}`}>
                <button
                    className="close-form-btn"
                    onClick={toggleSectionForm}
                    aria-label="Cerrar formulario"
                >
                    ×
                </button>

                <div className="form-content">
                    <h4 className="mb-4">Nueva Sección</h4>

                    {sectionFormError && (
                        <div className="alert alert-danger" role="alert">
                            {sectionFormError}
                        </div>
                    )}

                    <form onSubmit={handleSectionSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Título de la sección</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ingrese el título"
                                value={sectionFormData.title}
                                onChange={(e) => handleSectionInputChange('title', e.target.value)}
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Recomendaciones</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: Antes del anteproyecto"
                                value={sectionFormData.recommendationText}
                                onChange={(e) => handleSectionInputChange('recommendationText', e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Tipo de sección</label>
                            <select
                                className="form-select"
                                value={sectionFormData.sectionType}
                                onChange={(e) => handleSectionInputChange('sectionType', e.target.value)}
                                required
                            >
                                <option value="TEXT">Texto</option>
                                <option value="QUESTION">Pregunta</option>
                            </select>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Tiempo aproximado</label>
                            <div className="d-flex gap-2">
                                <div className="flex-fill">
                                    <label className="form-label small text-muted">Horas</label>
                                    <select
                                        className="form-select"
                                        value={sectionFormData.hours}
                                        onChange={(e) => handleSectionInputChange('hours', e.target.value)}
                                    >
                                        {[...Array(4)].map((_, i) => (
                                            <option key={i} value={i}>{i}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-fill">
                                    <label className="form-label small text-muted">Minutos</label>
                                    <select
                                        className="form-select"
                                        value={sectionFormData.minutes}
                                        onChange={(e) => handleSectionInputChange('minutes', e.target.value)}
                                    >
                                        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(min => (
                                            <option key={min} value={min}>{min}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">¿En qué consiste?</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Describe en qué consiste esta sección"
                                value={sectionFormData.summary}
                                onChange={(e) => handleSectionInputChange('summary', e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">¿Para qué sirve?</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Explica para qué sirve esta sección"
                                value={sectionFormData.purpose}
                                onChange={(e) => handleSectionInputChange('purpose', e.target.value)}
                            />
                        </div>

                        <div className="d-flex justify-content-between mt-4">
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-cancel-custom" onClick={toggleSectionForm}>Cancelar</button>
                                <button type="submit" className="btn btn-create-custom">Crear</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {isSectionFormOpen && <div className="form-overlay" onClick={toggleSectionForm}></div>}

            {/* Section Content Manager Sidebar */}
            <div className={`submission-form-sidebar section-content-form ${isSectionContentManagerOpen ? 'open' : ''}`}>
                <button
                    className="close-form-btn"
                    onClick={closeSectionContentManager}
                    aria-label="Cerrar gestor de contenido"
                >
                    ×
                </button>

                <div className="form-content flex-grow-1 d-flex flex-column">
                    {!isSectionContentFormOpen ? (
                        showContentTypeSelection ? (
                            // Content Type Selector (replaces the list, like admin)
                            <>
                                <div className="flex-grow-1">
                                    <h4 className="mb-4">Selecciona el tipo de contenido a añadir</h4>
                                    <div className="row g-2">
                                        {([
                                            { type: 'TEXT_SIMPLE' as const, name: 'Texto Simple', icon: 'bi-file-text', tooltip: 'Texto en el centro de la pantalla' },
                                            { type: 'QUESTION_SIMPLE' as const, name: 'Pregunta Abierta', icon: 'bi-chat-text', tooltip: 'El usuario debe responder con texto.' },
                                            { type: 'QUESTION_MULTIPLE' as const, name: 'Pregunta tipo test', icon: 'bi-list-check', tooltip: 'Pregunta tipo test' },
                                            { type: 'IMAGE_COMMENT' as const, name: 'Subir imagen y comentar', icon: 'bi-upload', tooltip: 'El usuario debe subir una imagen y comentarla' },
                                        ]).map((opt) => (
                                            <div key={opt.type} className="col-6 col-md-4">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-dark w-100 d-flex flex-column align-items-center p-3"
                                                    onClick={() => {
                                                        setShowContentTypeSelection(false);
                                                        setEditingSectionContentItem(null);
                                                        setSectionContentFormData({
                                                            name: '',
                                                            type: opt.type,
                                                            content: '',
                                                            is_editable: false
                                                        });
                                                        setIsSectionContentFormOpen(true);
                                                    }}
                                                    title={opt.tooltip}
                                                    style={{ minHeight: '80px', transition: 'all 0.2s ease', backgroundColor: 'white' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.backgroundColor = 'white'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = 'white'; }}
                                                >
                                                    <i className={`bi ${opt.icon} fs-4 mb-2 text-dark`}></i>
                                                    <small className="text-center text-dark">{opt.name}</small>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="border-top pt-3 mt-4">
                                    <div className="d-flex justify-content-between">
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-cancel-custom"
                                                onClick={() => setShowContentTypeSelection(false)}
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : isViewingContent ? (
                            // Content Viewer for read-only content
                            <ContentViewerReadOnly
                                item={viewingContentItem}
                                onBack={closeContentViewer}
                            />
                        ) : (
                            // Section Content List
                            <>
                                <div className="flex-grow-1">
                                    <h4 className="mb-4">
                                        Contenido de Sección: {editingSectionContentSection?.sectionName}
                                    </h4>

                                    {sectionContentManagerError && (
                                        <div className="alert alert-danger" role="alert">
                                            {sectionContentManagerError}
                                        </div>
                                    )}

                                    <div className="section-content-list">
                                    {isLoadingSectionContent ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-dark mb-3" role="status">
                                                <span className="visually-hidden">Cargando...</span>
                                            </div>
                                            <div className="text-muted">Cargando contenido de la sección...</div>
                                        </div>
                                    ) : sectionContentItems.length === 0 ? (
                                        <div className="text-center text-muted py-4">
                                            <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                                            <div className="mb-3">No hay contenido en esta sección</div>
                                            <button
                                                className="btn btn-create-custom"
                                                onClick={() => setShowContentTypeSelection(true)}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Añadir Contenido
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {sectionContentItems.map((item, index) => (
                                                <div
                                                    key={item.projectSectionContentId || `item-${index}`}
                                                    className="section-content-item mb-3 p-3 border rounded"
                                                    style={{ position: 'relative' }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div className="d-flex align-items-start gap-2 flex-grow-1">
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                    <i className={`${getContentTypeInfo(item.contentType || item.type || 'TEXT_SIMPLE').icon} text-dark`}></i>
                                                                    <h6 className="mb-0">{item.sectionName || item.name}</h6>
                                                                </div>
                                                                <small className="text-muted">
                                                                    {getContentTypeInfo(item.contentType || item.type || 'TEXT_SIMPLE').name}
                                                                </small>
                                                                {getContentPreview(item) && (
                                                                    <div className="text-muted small mt-1" style={{
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical' as const,
                                                                        lineHeight: '1.4'
                                                                    }}>
                                                                        {getContentPreview(item)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="d-flex gap-1 align-items-start">
                                                            {isCustomContent(item) && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link text-muted p-1"
                                                                        onClick={() => moveContent(index, 'up')}
                                                                        disabled={index === 0}
                                                                        title="Mover arriba"
                                                                    >
                                                                        <i className="bi bi-chevron-up"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link text-muted p-1"
                                                                        onClick={() => moveContent(index, 'down')}
                                                                        disabled={index === sectionContentItems.length - 1}
                                                                        title="Mover abajo"
                                                                    >
                                                                        <i className="bi bi-chevron-down"></i>
                                                                    </button>
                                                                </>
                                                            )}
                                                            {(item.isEditable || item.is_editable) ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-dark"
                                                                    onClick={() => openSectionContentForm(item)}
                                                                    title="Editar"
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-dark"
                                                                    onClick={() => openContentViewer(item)}
                                                                    title="Ver contenido"
                                                                >
                                                                    <i className="bi bi-search"></i>
                                                                </button>
                                                            )}
                                                            {isCustomContent(item) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link text-danger p-1"
                                                                    onClick={async () => {
                                                                        if (!window.confirm('¿Eliminar este contenido?')) return;
                                                                        try {
                                                                            await apiClient.delete(`/quiz/project-section-contents/${item.projectSectionContentId}`);
                                                                            setSectionContentItems(prev => prev.filter(i => i.projectSectionContentId !== item.projectSectionContentId));
                                                                        } catch (err) {
                                                                            console.error('Error deleting content:', err);
                                                                        }
                                                                    }}
                                                                    title="Eliminar"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Include/Exclude Toggle - positioned bottom-right */}
                                                    {(item.isEditable || item.is_editable) && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: '12px',
                                                            right: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}>
                                                            <label
                                                                className="form-check-label small"
                                                                style={{
                                                                    color: item.isIncluded !== false ? '#000' : 'var(--color-primary)',
                                                                    fontWeight: '400',
                                                                    margin: 0
                                                                }}
                                                            >
                                                                {item.isIncluded !== false ? 'Incluido' : 'No incluido'}
                                                            </label>
                                                            <div className="form-check form-switch" style={{ margin: 0 }}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    checked={item.isIncluded !== false}
                                                                    onChange={() => {
                                                                        if (item.projectSectionContentId) {
                                                                            toggleContentIncluded(
                                                                                item.projectSectionContentId,
                                                                                item.isIncluded !== false
                                                                            );
                                                                        }
                                                                    }}
                                                                    title={item.isIncluded !== false ? 'Contenido incluido' : 'Contenido excluido'}
                                                                    style={{
                                                                        backgroundColor: item.isIncluded !== false ? '#28a745' : undefined,
                                                                        borderColor: item.isIncluded !== false ? '#28a745' : undefined
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {/* Add content button */}
                                            {!showContentTypeSelection && (
                                                <div className="text-center mt-3">
                                                    <button
                                                        className="btn btn-create-custom"
                                                        onClick={() => setShowContentTypeSelection(true)}
                                                    >
                                                        <i className="bi bi-plus-circle me-2"></i>
                                                        Añadir Contenido
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                        )
                    ) : (
                        // Section Content Form
                        <>
                            <div className="d-flex align-items-center mb-4">
                                <button
                                    type="button"
                                    className="btn btn-link p-0 me-3"
                                    onClick={() => {
                                        setIsSectionContentFormOpen(false);
                                        setEditingSectionContentItem(null);
                                        setSectionContentFormError('');
                                    }}
                                >
                                    <i className="bi bi-arrow-left fs-4"></i>
                                </button>
                                <h4 className="mb-0">
                                    {editingSectionContentItem ? 'Editar Contenido' : 'Nuevo Contenido'}
                                </h4>
                            </div>

                            {sectionContentFormError && (
                                <div className="alert alert-danger" role="alert">
                                    {sectionContentFormError}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="sectionContentName" className="form-label">Nombre del contenido</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="sectionContentName"
                                    value={sectionContentFormData.name}
                                    onChange={(e) => setSectionContentFormData(prev => ({
                                        ...prev,
                                        name: e.target.value
                                    }))}
                                    placeholder="Ingrese el nombre del contenido"
                                    disabled={!!editingSectionContentItem?.projectSectionContentId}
                                />
                            </div>

                            <div className="mb-3">
                                {sectionContentFormData.type === 'TEXT_SIMPLE' ? (
                                    <TextSimpleEditor
                                        content={sectionContentFormData.content}
                                        onChange={(newContent) => setSectionContentFormData(prev => ({
                                            ...prev,
                                            content: newContent
                                        }))}
                                        isEditable={true}
                                        onEditableChange={() => { }}
                                        showEditableToggle={false}
                                    />
                                ) : sectionContentFormData.type === 'TEXT_SELECTABLE' ? (
                                    sectionContentFormData.is_editable ? (
                                        <TextSelectableArchitectEditor
                                            content={sectionContentFormData.content}
                                            onChange={(newContent) => setSectionContentFormData(prev => ({
                                                ...prev,
                                                content: newContent
                                            }))}
                                            isEditable={true}
                                            onEditableChange={() => { }}
                                            showEditableToggle={false}
                                            projectClientSize={project?.clientSize}
                                            projectClientTone={project?.clientTone}
                                        />
                                    ) : (
                                        <TextSelectableEditor
                                            content={sectionContentFormData.content}
                                            onChange={(newContent) => setSectionContentFormData(prev => ({
                                                ...prev,
                                                content: newContent
                                            }))}
                                            isEditable={false}
                                            onEditableChange={() => { }}
                                            allowAddingOptions={false}
                                            showEditableToggle={false}
                                        />
                                    )
                                ) : sectionContentFormData.type === 'QUESTION_SIMPLE' ? (
                                    <QuestionSimpleEditor
                                        content={sectionContentFormData.content}
                                        onChange={(newContent) => setSectionContentFormData(prev => ({
                                            ...prev,
                                            content: newContent
                                        }))}
                                        isEditable={true}
                                        onEditableChange={() => { }}
                                        showEditableToggle={false}
                                    />
                                ) : sectionContentFormData.type === 'QUESTION_MULTIPLE' ? (
                                    (() => {
                                        const qmContent = (sectionContentFormData.content && typeof sectionContentFormData.content === 'object')
                                            ? sectionContentFormData.content as any
                                            : { questionText: sectionContentFormData.content || '', answers: [], allowMultiple: false };
                                        return (
                                            <QuestionMultipleEditor
                                                content={{
                                                    questionText: qmContent.questionText ?? '',
                                                    questionImage: qmContent.questionImage,
                                                    answers: Array.isArray(qmContent.answers) ? qmContent.answers : [],
                                                    allowMultiple: !!qmContent.allowMultiple,
                                                }}
                                                onChange={(newContent) => setSectionContentFormData(prev => ({
                                                    ...prev,
                                                    content: newContent
                                                }))}
                                                testQuestionImage={qmContent.questionImage || ''}
                                                onQuestionImageChange={(image) => setSectionContentFormData(prev => {
                                                    const cur = (prev.content && typeof prev.content === 'object') ? prev.content as any : {};
                                                    return { ...prev, content: { ...cur, questionImage: image } };
                                                })}
                                                allowMultipleAnswers={!!qmContent.allowMultiple}
                                                onAllowMultipleChange={(allow) => setSectionContentFormData(prev => {
                                                    const cur = (prev.content && typeof prev.content === 'object') ? prev.content as any : {};
                                                    return { ...prev, content: { ...cur, allowMultiple: allow } };
                                                })}
                                                isEditable={true}
                                                onEditableChange={() => { }}
                                                showEditableToggle={false}
                                            />
                                        );
                                    })()
                                ) : sectionContentFormData.type === 'QUESTION_IMAGE' ? (
                                    <QuestionImageEditor
                                        content={{
                                            text: sectionContentFormData.content,
                                            imageUrl: '',
                                            imageFilename: ''
                                        }}
                                        onChange={(newContent) => setSectionContentFormData(prev => ({
                                            ...prev,
                                            content: newContent.text
                                        }))}
                                        uploadedImageUrl=""
                                        onImageUrlChange={() => { }}
                                        uploadedImageFilename=""
                                        onImageFilenameChange={() => { }}
                                        isUploadingImage={false}
                                        onImageUpload={() => { }}
                                        onImageDelete={() => { }}
                                        isEditable={true}
                                        onEditableChange={() => { }}
                                        showEditableToggle={false}
                                    />
                                ) : (
                                    <div>
                                        <label className="form-label">Contenido</label>
                                        <textarea
                                            className="form-control"
                                            rows={8}
                                            value={typeof sectionContentFormData.content === 'string' ? sectionContentFormData.content : JSON.stringify(sectionContentFormData.content, null, 2)}
                                            onChange={(e) => setSectionContentFormData(prev => ({
                                                ...prev,
                                                content: e.target.value
                                            }))}
                                            placeholder="Ingrese el contenido..."
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-between mt-4">
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-cancel-custom"
                                        onClick={() => {
                                            setIsSectionContentFormOpen(false);
                                            setEditingSectionContentItem(null);
                                            setSectionContentFormError('');
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-create-custom"
                                        onClick={saveSectionContentItem}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {isSectionContentManagerOpen && <div className="form-overlay" onClick={closeSectionContentManager}></div>}

            {/* Submit Confirmation Modal */}
            {isSubmitModalOpen && groupToSubmit && (
                <>
                    <div className="form-overlay" onClick={closeSubmitModal} style={{ zIndex: 1060 }}></div>
                    <div
                        className="modal fade show"
                        style={{
                            display: 'block',
                            zIndex: 1061
                        }}
                        tabIndex={-1}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar envío de grupo</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeSubmitModal}
                                        disabled={isSubmittingGroup}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {submitError && (
                                        <div className="alert alert-danger" role="alert">
                                            {submitError}
                                        </div>
                                    )}

                                    <h6 className="mb-3">{groupToSubmit.groupName}</h6>

                                    <p className="mb-3">
                                        Estás a punto de enviar este grupo. Se incluirán las siguientes secciones:
                                    </p>

                                    <ul className="list-group mb-3">
                                        {getActiveSections(groupToSubmit).map(section => (
                                            <li
                                                key={section.projectSectionId}
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                            >
                                                <span>
                                                    <i className={`bi ${
                                                        section.sectionType === 'TEXT' ? 'bi-justify' :
                                                        section.sectionType === 'QUESTION' ? 'bi-question-lg' :
                                                        section.sectionType === 'CHALLENGE' ? 'bi-puzzle' :
                                                        'bi-justify'
                                                    } me-2`}></i>
                                                    {section.sectionName}
                                                </span>
                                                <span className="badge bg-success">
                                                    <i className="bi bi-check-circle me-1"></i>
                                                    Listo
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="alert alert-info mb-0">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Una vez enviado, el grupo quedará marcado como completado.
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={closeSubmitModal}
                                        disabled={isSubmittingGroup}
                                        style={{
                                            flex: 1,
                                            backgroundColor: 'transparent',
                                            color: '#000',
                                            border: '1px solid #000',
                                            borderRadius: 0,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={handleGroupSubmit}
                                        disabled={isSubmittingGroup}
                                        style={{
                                            flex: 1,
                                            backgroundColor: isSubmittingGroup ? '#ccc' : '#000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 0,
                                            textTransform: 'uppercase',
                                            cursor: isSubmittingGroup ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {isSubmittingGroup ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Enviando...
                                            </>
                                        ) : (
                                            'Confirmar envío'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Include Content Confirmation Modal */}
            {isIncludeModalOpen && contentToInclude && (
                <>
                    <div className="form-overlay" onClick={closeIncludeModal} style={{ zIndex: 1060 }}></div>
                    <div
                        className="modal fade show"
                        style={{
                            display: 'block',
                            zIndex: 1061
                        }}
                        tabIndex={-1}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar inclusión de contenido</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeIncludeModal}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="alert alert-warning mb-3">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        Por favor, confirma que has revisado el contenido antes de incluirlo y que lo das por válido.
                                    </div>

                                    {(() => {
                                        const contentType = contentToInclude.contentType || contentToInclude.type;
                                        const fullText = getFullContentText(contentToInclude);

                                        if ((contentType === 'TEXT_SIMPLE' || contentType === 'QUESTION_SIMPLE' || contentType === 'QUESTION_MULTIPLE') && fullText) {
                                            return (
                                                <div className="p-3 bg-light border rounded">
                                                    <div dangerouslySetInnerHTML={{ __html: fullText }} />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={closeIncludeModal}
                                        style={{
                                            flex: 1,
                                            backgroundColor: 'transparent',
                                            color: '#000',
                                            border: '1px solid #000',
                                            borderRadius: 0,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => {
                                            if (contentToInclude.projectSectionContentId) {
                                                confirmContentInclusion(
                                                    contentToInclude.projectSectionContentId,
                                                    contentToInclude.isIncluded !== false
                                                );
                                            }
                                        }}
                                        style={{
                                            flex: 1,
                                            backgroundColor: '#000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 0,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Incluir
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Enable Section Confirmation Modal */}
            {isEnableSectionModalOpen && sectionToEnable && (
                <>
                    <div className="form-overlay" onClick={closeSectionEnableModal} style={{ zIndex: 1060 }}></div>
                    <div
                        className="modal fade show"
                        style={{
                            display: 'block',
                            zIndex: 1061
                        }}
                        tabIndex={-1}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar habilitación de sección</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeSectionEnableModal}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <h6 className="mb-3">{sectionToEnable.sectionName}</h6>

                                    <div className="alert alert-warning mb-3">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        Por favor, confirma que has revisado el contenido de esta sección antes de habilitarla.
                                    </div>

                                    {/* Lista de contenidos de la sección */}
                                    {sectionContentItems.length > 0 && (
                                        <div className="mb-3">
                                            <h6 className="mb-3">Contenidos de la sección:</h6>
                                            <div>
                                                {sectionContentItems.map((item, index) => (
                                                    <div key={item.projectSectionContentId || index}>
                                                        <div className="d-flex justify-content-between align-items-start py-2">
                                                            <div className="flex-grow-1">
                                                                <div className="mb-1">{item.sectionName || item.name}</div>
                                                                <small className="text-muted">
                                                                    {getContentTypeInfo(item.contentType || item.type || 'TEXT_SIMPLE').name}
                                                                </small>
                                                            </div>
                                                            <div style={{
                                                                fontSize: '0.875rem',
                                                                fontWeight: 'normal',
                                                                color: item.isIncluded !== false ? '#28a745' : '#6c757d',
                                                                whiteSpace: 'nowrap',
                                                                marginLeft: '12px'
                                                            }}>
                                                                {item.isIncluded !== false ? 'Incluida' : 'No incluida'}
                                                            </div>
                                                        </div>
                                                        {index < sectionContentItems.length - 1 && (
                                                            <hr style={{ margin: '0.5rem 0', borderColor: '#e0e0e0' }} />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    {/* Verificar si todos los contenidos están desactivados */}
                                    {(() => {
                                        const allNotIncluded = sectionContentItems.length > 0 &&
                                            sectionContentItems.every(item => item.isIncluded === false);

                                        return (
                                            <>
                                                {allNotIncluded && (
                                                    <div className="w-100 mb-2 text-center">
                                                        <small className="text-danger">
                                                            <i className="bi bi-exclamation-circle me-1"></i>
                                                            Debes incluir al menos un contenido para habilitar esta sección
                                                        </small>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn"
                                                    onClick={closeSectionEnableModal}
                                                    style={{
                                                        flex: 1,
                                                        backgroundColor: 'transparent',
                                                        color: '#000',
                                                        border: '1px solid #000',
                                                        borderRadius: 0,
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn"
                                                    onClick={() => {
                                                        if (sectionToEnable) {
                                                            confirmSectionEnable(sectionToEnable);
                                                        }
                                                    }}
                                                    disabled={allNotIncluded}
                                                    style={{
                                                        flex: 1,
                                                        backgroundColor: allNotIncluded ? '#ccc' : '#000',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: 0,
                                                        textTransform: 'uppercase',
                                                        cursor: allNotIncluded ? 'not-allowed' : 'pointer',
                                                        opacity: allNotIncluded ? 0.6 : 1
                                                    }}
                                                >
                                                    Habilitar
                                                </button>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Delivery Modal */}
            {isDeliveryModalOpen && (
                <>
                    <div className="form-overlay" onClick={closeDeliveryModal} style={{ zIndex: 1060 }}></div>
                    <div
                        className="modal fade show"
                        style={{
                            display: 'block',
                            zIndex: 1061
                        }}
                        tabIndex={-1}
                    >
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '700px' }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Enviar entregas</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeDeliveryModal}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {/* List of pending groups */}
                                    <div className="mb-4">
                                        <p className="mb-2"><strong>Grupos a enviar:</strong></p>
                                        <ul className="mb-0">
                                            {getPendingDeliveryGroups().map(group => (
                                                <li key={group.projectGroupId}>
                                                    {group.groupName}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Comments textarea */}
                                    <div className="mb-3">
                                        <label htmlFor="deliveryComments" className="form-label">
                                            <strong>Comentarios (opcional):</strong>
                                        </label>
                                        <textarea
                                            id="deliveryComments"
                                            className="form-control"
                                            rows={8}
                                            value={deliveryComments}
                                            onChange={(e) => setDeliveryComments(e.target.value)}
                                            placeholder="Añade comentarios personalizados para este envío..."
                                            disabled={isSendingDelivery}
                                            style={{
                                                resize: 'vertical',
                                                minHeight: '150px'
                                            }}
                                        />
                                    </div>

                                    {/* Error message */}
                                    {deliveryError && (
                                        <div className="alert alert-danger mb-0" role="alert">
                                            <i className="bi bi-exclamation-circle me-2"></i>
                                            {deliveryError}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={closeDeliveryModal}
                                        disabled={isSendingDelivery}
                                        style={{
                                            flex: 1,
                                            backgroundColor: 'transparent',
                                            color: '#000',
                                            border: '1px solid #000',
                                            borderRadius: 0,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={handleSendDeliveries}
                                        disabled={isSendingDelivery}
                                        style={{
                                            flex: 1,
                                            backgroundColor: isSendingDelivery ? '#ccc' : '#000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 0,
                                            textTransform: 'uppercase',
                                            cursor: isSendingDelivery ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {isSendingDelivery ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Enviando...
                                            </>
                                        ) : (
                                            'Enviar'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ProjectDetail;
